import React, {useState} from "react"
import {CheckPayment} from "../../resources/checkPayments"
import {cancelCheckPayment} from "../../resources/checkPayments"
import {useAccessToken} from "../../services/auth"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {Modal} from "../Modals/Modal"

interface CancelCheckPaymentModalProps {
	checkPayment: CheckPayment
	closeModal: () => void
	onSuccess: () => void
}

export function CancelCheckPaymentModal({checkPayment, closeModal, onSuccess}: CancelCheckPaymentModalProps) {
	const accessToken = useAccessToken()
	const [error, setError] = useState("")
	const [cancelState, cancelAction] = useAsyncResultIdle(cancelCheckPayment)

	const handleCancel = async () => {
		setError("")
		const result = await cancelAction({
			id: checkPayment.id,
			accessToken,
		})

		result.match(
			() => {
				onSuccess()
			},
			(err) => {
				const msg = err.errors?.[0]?.detail || "Failed to cancel check payment"
				setError(msg)
			}
		)
	}

	const isLoading = cancelState.isPending()

	return (
		<Modal
			close={() => {
				closeModal()
			}}
			classname="check-payment-modal"
		>
			<div className="modal-content">
				<h3 className="title is-4">You are about to cancel this check</h3>
				<p className="mb-4">
					Cancelling a check prevents it from being cashed or deposited. This action is final and may have legal or
					financial implications.
				</p>

				<p className="mb-4 has-text-weight-semibold">Are you sure you want to proceed?</p>

				<table className="table is-fullwidth check-payment-table">
					<thead>
						<tr>
							<th>ID</th>
							<th>Check Number</th>
							<th>Purpose</th>
							<th>Amount</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>{checkPayment.id}</td>
							<td>{checkPayment.attributes.checkNumber || "N/A"}</td>
							<td>{checkPayment.attributes.isAccountClosureCheck ? "Account Closure" : "General Use"}</td>
							<td>${(checkPayment.attributes.amount / 100).toFixed(2)}</td>
						</tr>
					</tbody>
				</table>

				{error && <div className="notification is-danger">{error}</div>}

				<div className="modal-actions">
					<div className="field is-grouped is-grouped-right">
						<div className="control">
							<button className="button do-not-button" onClick={closeModal} disabled={isLoading}>
								Do Not Cancel
							</button>
						</div>
						<div className="control">
							<button
								className={`button is-black ${isLoading ? "is-loading" : ""}`}
								onClick={handleCancel}
								disabled={isLoading}
							>
								Cancel Check
							</button>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	)
}
